/**
 * Print Area Configuration
 *
 * Defines print areas based on real-world measurements (inches)
 * and converts them to canvas pixel coordinates.
 *
 * Canvas dimensions: 600px wide x 700px tall
 */

// Conversion factor: pixels per inch on canvas
// We'll use body width as our reference point
// For a Medium shirt (22" wide), we want it to fit nicely on 600px canvas
// Let's assume the garment image takes ~500px width on canvas (with margins)
// 500px = 22" → ~22.7 pixels per inch
const PIXELS_PER_INCH = 22.7;

/**
 * Garment size specifications (from size guides)
 * All measurements in inches
 */
export const GARMENT_SPECS = {
  '5026': { // Mens Classic Tee
    name: 'Classic Tee',
    sizes: {
      SML: { bodyWidth: 18.5, bodyLength: 27.5 },
      MED: { bodyWidth: 20.5, bodyLength: 29 },
      LRG: { bodyWidth: 22.25, bodyLength: 30.25 },
      XLG: { bodyWidth: 24, bodyLength: 32 },
      '2XL': { bodyWidth: 25.5, bodyLength: 33.75 },
      '3XL': { bodyWidth: 27.5, bodyLength: 34.25 },
      '4XL': { bodyWidth: 29.25, bodyLength: 34.75 },
      '5XL': { bodyWidth: 31, bodyLength: 35 },
    },
  },
  '5026T': { // Mens Classic Tear Out Tee
    name: 'Classic Tear Out Tee',
    sizes: {
      SML: { bodyWidth: 18.5, bodyLength: 27.5 },
      MED: { bodyWidth: 20.5, bodyLength: 29 },
      LRG: { bodyWidth: 22.25, bodyLength: 30.25 },
      XLG: { bodyWidth: 24, bodyLength: 32 },
      '2XL': { bodyWidth: 25.25, bodyLength: 33.75 },
      '3XL': { bodyWidth: 26.75, bodyLength: 34.25 },
    },
  },
  '5030': { // Box Tee
    name: 'Box Tee',
    sizes: {
      SML: { bodyWidth: 20.5, bodyLength: 26.25 },
      MED: { bodyWidth: 22, bodyLength: 27.25 },
      LRG: { bodyWidth: 23.5, bodyLength: 28.25 },
      XLG: { bodyWidth: 25, bodyLength: 29.25 },
      '2XL': { bodyWidth: 26.75, bodyLength: 30.25 },
      '3XL': { bodyWidth: 28.25, bodyLength: 31.25 },
    },
  },
  '5040': { // Mens Stone Wash Staple Tee
    name: 'Stone Wash Staple Tee',
    sizes: {
      SML: { bodyWidth: 18.5, bodyLength: 28 },
      MED: { bodyWidth: 20.5, bodyLength: 29.5 },
      LRG: { bodyWidth: 22.25, bodyLength: 31 },
      XLG: { bodyWidth: 24, bodyLength: 32.25 },
      '2XL': { bodyWidth: 25.25, bodyLength: 33 },
      '3XL': { bodyWidth: 26.5, bodyLength: 33.75 },
    },
  },
  '5069': { // Classic Oversized Tee
    name: 'Classic Oversized Tee',
    sizes: {
      SML: { bodyWidth: 23.5, bodyLength: 27 },
      MED: { bodyWidth: 24.75, bodyLength: 28.25 },
      LRG: { bodyWidth: 26, bodyLength: 29.5 },
      XLG: { bodyWidth: 27.25, bodyLength: 30.5 },
      '2XL': { bodyWidth: 28.25, bodyLength: 31.5 },
      '3XL': { bodyWidth: 29.5, bodyLength: 32.75 },
    },
  },
  '5085': { // Mens Stone Wash Heavy Tee
    name: 'Stone Wash Heavy Tee',
    sizes: {
      SML: { bodyWidth: 20, bodyLength: 28.25 },
      MED: { bodyWidth: 21.75, bodyLength: 29.5 },
      LRG: { bodyWidth: 23.25, bodyLength: 31 },
      XLG: { bodyWidth: 24.75, bodyLength: 32.25 },
      '2XL': { bodyWidth: 26.5, bodyLength: 33.75 },
      '3XL': { bodyWidth: 28, bodyLength: 34.5 },
    },
  },
  '5086': { // Mens Heavy Faded Minus Tee
    name: 'Heavy Faded Minus Tee',
    sizes: {
      SML: { bodyWidth: 20, bodyLength: 26.25 },
      MED: { bodyWidth: 21.5, bodyLength: 27.5 },
      LRG: { bodyWidth: 23.25, bodyLength: 29 },
      XLG: { bodyWidth: 24.75, bodyLength: 30 },
      '2XL': { bodyWidth: 26.25, bodyLength: 31 },
      '3XL': { bodyWidth: 28, bodyLength: 32 },
    },
  },
  '5150': { // Made Crew
    name: 'Made Crew',
    sizes: {
      SML: { bodyWidth: 23, bodyLength: 27.25 },
      MED: { bodyWidth: 24.5, bodyLength: 28 },
      LRG: { bodyWidth: 25.5, bodyLength: 29 },
      XLG: { bodyWidth: 26.5, bodyLength: 30 },
      '2XL': { bodyWidth: 27.75, bodyLength: 31 },
      '3XL': { bodyWidth: 29, bodyLength: 32 },
    },
  },
  '5151': { // Made Hood
    name: 'Made Hood',
    sizes: {
      SML: { bodyWidth: 23, bodyLength: 27.25 },
      MED: { bodyWidth: 24.5, bodyLength: 28 },
      LRG: { bodyWidth: 25.5, bodyLength: 29 },
      XLG: { bodyWidth: 26.5, bodyLength: 30 },
      '2XL': { bodyWidth: 27.75, bodyLength: 31 },
      '3XL': { bodyWidth: 29, bodyLength: 32 },
    },
  },
  '5165': { // Mens Relax Faded Crew
    name: 'Relax Faded Crew',
    sizes: {
      XSM: { bodyWidth: 21.25, bodyLength: 26.5 },
      SML: { bodyWidth: 22.5, bodyLength: 27.5 },
      MED: { bodyWidth: 23.5, bodyLength: 28.5 },
      LRG: { bodyWidth: 24.75, bodyLength: 29.5 },
      XLG: { bodyWidth: 26, bodyLength: 30.5 },
      '2XL': { bodyWidth: 27.25, bodyLength: 31.5 },
      '3XL': { bodyWidth: 28.25, bodyLength: 32.5 },
    },
  },
  '5512': { // Mens Felt Bomber (jacket - uses Half Chest measurement)
    name: 'Felt Bomber Jacket',
    sizes: {
      SML: { bodyWidth: 23, bodyLength: 27 },
      MED: { bodyWidth: 24.25, bodyLength: 28 },
      LRG: { bodyWidth: 25.5, bodyLength: 29 },
      XLG: { bodyWidth: 26.5, bodyLength: 30 },
      '2XL': { bodyWidth: 27.25, bodyLength: 31 },
      '3XL': { bodyWidth: 29, bodyLength: 32 },
    },
  },
};

/**
 * Standard print area templates (in inches)
 * Based on industry-standard screen printing frame sizes
 *
 * Common screen sizes in the industry:
 * - 10" x 12" - Small logos, pocket prints
 * - 12" x 14" - Standard chest/front print
 * - 14" x 16" - Large front, standard back
 * - 16" x 18" - Full front/back
 * - 18" x 20" - Oversized prints
 * - 20" x 24" - Full garment prints
 */
export const PRINT_AREA_TEMPLATES = {
  // Pocket/Left Chest prints
  POCKET: {
    name: 'Pocket Print',
    widthInches: 3.5,
    heightInches: 3.5,
    description: 'Pocket print (3.5" x 3.5")',
    screenSize: 'Small',
    category: 'chest',
  },

  LEFT_CHEST: {
    name: 'Left Chest Logo',
    widthInches: 4,
    heightInches: 4,
    description: 'Left chest logo (4" x 4")',
    screenSize: 'Small',
    category: 'chest',
  },

  // Small prints
  SMALL_SQUARE: {
    name: 'Small Square',
    widthInches: 8,
    heightInches: 8,
    description: 'Small square print (8" x 8")',
    screenSize: '10" x 12"',
    category: 'front',
  },

  SMALL_PORTRAIT: {
    name: 'Small Portrait',
    widthInches: 8,
    heightInches: 10,
    description: 'Small portrait (8" x 10")',
    screenSize: '10" x 12"',
    category: 'front',
  },

  // Standard prints - Most common
  STANDARD: {
    name: 'Standard Front',
    widthInches: 12,
    heightInches: 14,
    description: 'Standard front print (12" x 14")',
    screenSize: '12" x 14"',
    category: 'front',
  },

  STANDARD_SQUARE: {
    name: 'Standard Square',
    widthInches: 12,
    heightInches: 12,
    description: 'Standard square (12" x 12")',
    screenSize: '12" x 14"',
    category: 'front',
  },

  STANDARD_WIDE: {
    name: 'Standard Wide',
    widthInches: 14,
    heightInches: 12,
    description: 'Standard wide (14" x 12")',
    screenSize: '14" x 16"',
    category: 'front',
  },

  // Large prints
  LARGE_FRONT: {
    name: 'Large Front',
    widthInches: 14,
    heightInches: 16,
    description: 'Large front print (14" x 16")',
    screenSize: '14" x 16"',
    category: 'front',
  },

  LARGE_SQUARE: {
    name: 'Large Square',
    widthInches: 14,
    heightInches: 14,
    description: 'Large square (14" x 14")',
    screenSize: '14" x 16"',
    category: 'front',
  },

  // Extra Large / Full prints
  XL_FRONT: {
    name: 'XL Front',
    widthInches: 16,
    heightInches: 18,
    description: 'Extra large front (16" x 18")',
    screenSize: '16" x 18"',
    category: 'front',
  },

  FULL_FRONT: {
    name: 'Full Front',
    widthInches: 16,
    heightInches: 20,
    description: 'Full front print (16" x 20")',
    screenSize: '18" x 20"',
    category: 'front',
  },

  // Back prints - typically larger
  BACK_STANDARD: {
    name: 'Standard Back',
    widthInches: 14,
    heightInches: 16,
    description: 'Standard back print (14" x 16")',
    screenSize: '14" x 16"',
    category: 'back',
  },

  BACK_LARGE: {
    name: 'Large Back',
    widthInches: 16,
    heightInches: 18,
    description: 'Large back print (16" x 18")',
    screenSize: '16" x 18"',
    category: 'back',
  },

  BACK_FULL: {
    name: 'Full Back',
    widthInches: 16,
    heightInches: 20,
    description: 'Full back print (16" x 20")',
    screenSize: '18" x 20"',
    category: 'back',
  },

  BACK_OVERSIZED: {
    name: 'Oversized Back',
    widthInches: 18,
    heightInches: 22,
    description: 'Oversized back (18" x 22")',
    screenSize: '20" x 24"',
    category: 'back',
  },

  // Sleeve prints
  SLEEVE: {
    name: 'Sleeve Print',
    widthInches: 3,
    heightInches: 4,
    description: 'Sleeve print (3" x 4")',
    screenSize: 'Small',
    category: 'sleeve',
  },

  SLEEVE_LARGE: {
    name: 'Large Sleeve',
    widthInches: 4,
    heightInches: 5,
    description: 'Large sleeve (4" x 5")',
    screenSize: '10" x 12"',
    category: 'sleeve',
  },

  // Special sizes
  OVERSIZED: {
    name: 'Oversized Print',
    widthInches: 18,
    heightInches: 20,
    description: 'Oversized print (18" x 20")',
    screenSize: '18" x 20"',
    category: 'front',
  },

  ALL_OVER: {
    name: 'All Over Print',
    widthInches: 20,
    heightInches: 24,
    description: 'All over print (20" x 24")',
    screenSize: '20" x 24"',
    category: 'front',
  },
};

/**
 * Get print templates by category
 */
export const getPrintTemplatesByCategory = (category = 'front') => {
  return Object.entries(PRINT_AREA_TEMPLATES)
    .filter(([key, template]) => template.category === category)
    .map(([key, template]) => ({
      id: key,
      ...template,
    }))
    .sort((a, b) => {
      // Sort by size (area)
      const areaA = a.widthInches * a.heightInches;
      const areaB = b.widthInches * b.heightInches;
      return areaA - areaB;
    });
};

/**
 * Get all print templates as array
 */
export const getAllPrintTemplates = () => {
  return Object.entries(PRINT_AREA_TEMPLATES).map(([key, template]) => ({
    id: key,
    ...template,
  }));
};

/**
 * Convert inches to canvas pixels
 */
export const inchesToPixels = (inches) => {
  return Math.round(inches * PIXELS_PER_INCH);
};

/**
 * Convert canvas pixels to inches
 */
export const pixelsToInches = (pixels) => {
  return (pixels / PIXELS_PER_INCH).toFixed(2);
};

/**
 * Get print area configuration for a specific garment and size
 * Returns print areas in canvas pixel coordinates
 */
export const getPrintAreasForGarment = (styleNumber, size = 'MED', view = 'front') => {
  const garmentSpec = GARMENT_SPECS[styleNumber];

  if (!garmentSpec) {
    console.warn(`No specs found for style ${styleNumber}, using defaults`);
    return getDefaultPrintAreas(view);
  }

  const sizeSpec = garmentSpec.sizes[size] || garmentSpec.sizes.MED;

  // Calculate print areas based on garment dimensions
  const printAreas = [];

  if (view === 'front') {
    // Front chest print - centered, positioned below neckline
    const template = PRINT_AREA_TEMPLATES.STANDARD;

    // Position: centered horizontally, ~4" from top
    const x = (600 - inchesToPixels(template.widthInches)) / 2;
    const y = inchesToPixels(4); // 4" from top of garment

    printAreas.push({
      name: 'Front',
      x: Math.round(x),
      y: Math.round(y),
      width: inchesToPixels(template.widthInches),
      height: inchesToPixels(template.heightInches),
      widthInches: template.widthInches,
      heightInches: template.heightInches,
      printCost: 2.00,
      description: template.description,
    });
  } else {
    // Back print - centered, positioned below collar
    const template = PRINT_AREA_TEMPLATES.BACK_STANDARD;

    // Position: centered horizontally, ~3" from top
    const x = (600 - inchesToPixels(template.widthInches)) / 2;
    const y = inchesToPixels(3); // 3" from top of garment

    printAreas.push({
      name: 'Back',
      x: Math.round(x),
      y: Math.round(y),
      width: inchesToPixels(template.widthInches),
      height: inchesToPixels(template.heightInches),
      widthInches: template.widthInches,
      heightInches: template.heightInches,
      printCost: 2.00,
      description: template.description,
    });
  }

  return printAreas;
};

/**
 * Default print areas (fallback when no specs available)
 */
const getDefaultPrintAreas = (view) => {
  const template = view === 'front'
    ? PRINT_AREA_TEMPLATES.STANDARD
    : PRINT_AREA_TEMPLATES.BACK_STANDARD;

  const x = (600 - inchesToPixels(template.widthInches)) / 2;
  const y = inchesToPixels(view === 'front' ? 4 : 3);

  return [{
    name: view === 'front' ? 'Front' : 'Back',
    x: Math.round(x),
    y: Math.round(y),
    width: inchesToPixels(template.widthInches),
    height: inchesToPixels(template.heightInches),
    widthInches: template.widthInches,
    heightInches: template.heightInches,
    printCost: 2.00,
    description: template.description,
  }];
};

/**
 * Calculate maximum print area for a garment
 * (useful for full-bleed prints)
 */
export const getMaxPrintArea = (styleNumber, size = 'MED') => {
  const garmentSpec = GARMENT_SPECS[styleNumber];

  if (!garmentSpec) {
    return null;
  }

  const sizeSpec = garmentSpec.sizes[size] || garmentSpec.sizes.MED;

  // Max print area = body width - 3" margins (1.5" each side)
  const maxWidth = sizeSpec.bodyWidth - 3;
  // Max height = body length - 6" (3" top, 3" bottom for collar/hem)
  const maxHeight = sizeSpec.bodyLength - 6;

  return {
    widthInches: maxWidth,
    heightInches: maxHeight,
    widthPixels: inchesToPixels(maxWidth),
    heightPixels: inchesToPixels(maxHeight),
  };
};

/**
 * Validate if artwork fits within print area
 */
export const validateArtworkSize = (artworkWidthPx, artworkHeightPx, printArea) => {
  const artworkWidthInches = pixelsToInches(artworkWidthPx);
  const artworkHeightInches = pixelsToInches(artworkHeightPx);

  const fitsWidth = artworkWidthPx <= printArea.width;
  const fitsHeight = artworkHeightPx <= printArea.height;

  return {
    valid: fitsWidth && fitsHeight,
    artworkWidthInches,
    artworkHeightInches,
    printAreaWidthInches: printArea.widthInches,
    printAreaHeightInches: printArea.heightInches,
    exceedsWidth: !fitsWidth,
    exceedsHeight: !fitsHeight,
  };
};
